import { useEffect, useMemo, useState } from "react";
import { blockStore } from "@/data/block-store";
import { collectionStore } from "@/data/collection-store";
export const COLLECTION_CARD_PREVIEW_LIMIT = 3;
/**
 * Up-to-3-envelope peek stack rendered on each `<CollectionBlockView>` /
 * `<StackBlockView>` card on the /collections grid.
 *
 * Reads from `collectionStore.useBlocks(collection.id, { limit: 3 })`,
 * which surfaces the per-collection block index. On first
 * intersection (`inView`), kicks off `loadBlocks` if the index is
 * empty — the loader upserts envelopes into `blockStore` AND writes
 * membership rows in one beat, so the live query reactively re-renders
 * the moment the data lands.
 *
 * Optimistic adds (`copyBlocks`, capture-into-collection) write
 * directly into the index, so the peek stack updates instantly without
 * any explicit invalidate / revision-key trick.
 */
export function useCollectionCardPreviews(collection, observeTargetRef, options) {
    const enabled = options?.enabled !== false;
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (enabled === false)
            return;
        const el = observeTargetRef.current;
        if (!el)
            return;
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting))
                setInView(true);
        }, { rootMargin: "160px", threshold: 0.02 });
        io.observe(el);
        return () => io.disconnect();
    }, [enabled, collection.id, observeTargetRef]);
    const blocksList = collectionStore.useBlocks(collection.id, {
        limit: COLLECTION_CARD_PREVIEW_LIMIT,
    });
    // First-paint loader: only on first observed intersection, only if
    // the index is empty for this scope. Optimistic writes from
    // `copyBlocks` etc. populate the index without a fetch, so a
    // collection that's been touched recently never round-trips.
    useEffect(() => {
        if (enabled === false)
            return;
        if (!inView)
            return;
        if (blocksList.rows.length > 0)
            return;
        void collectionStore.loadBlocks(collection.id, {
            limit: COLLECTION_CARD_PREVIEW_LIMIT,
        });
        // Run once per (id, inView) — `blocksList.rows.length` re-checks
        // happen via the live query without spinning loadBlocks again.
    }, [enabled, collection.id, inView, blocksList.rows.length]);
    const envelopes = useMemo(() => blocksList.rows.map((row) => {
        const cached = blockStore.get(row.blockId);
        if (cached)
            return cached;
        return {
            block: {
                id: row.blockId,
                type: "text",
                content: { text: "" },
                collectionId: "",
                createdBy: "",
                lineageId: "",
                title: "",
                summary: "",
                metadata: { channel: "viewer_client" },
                ingestorVersion: "0",
                processingStatus: "pending",
                capturedAt: row.sortKey,
                createdAt: row.sortKey,
                surfaceAt: row.sortKey,
            },
        };
    }), [blocksList.rows]);
    // Total item count is authoritative from `collection.blockCount`
    // (set by the `/collection/list` endpoint) — never derive it from
    // the index, which only holds the loaded slice.
    const count = collection.blockCount ?? envelopes.length;
    return {
        envelopes,
        count,
        isLoading: blocksList.isLoading,
        isError: false,
    };
}
